import React, { useMemo } from "react";
import { Calendar, Clock, User, ArrowRight, Share2, Tag, ChevronRight } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";


const formatDate = (dateString?: string) => {
  if (!dateString) return "";
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};


function RelatedPostsConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div data-section-id="1406" className="grid grid-cols-1 gap-8 md:grid-cols-3">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="h-[400px] rounded-xl bg-muted/20 animate-pulse" data-index={i} />
        ))}
      </div>
    );
  }

  if (error || posts.length === 0) {
    return null;
  }

  return (
    <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
      {posts.map((post, i) => (
        <Card key={post.id} className="flex flex-col h-full border border-border/50 bg-card/50 backdrop-blur-sm shadow-xl overflow-hidden group transition-all duration-300 hover:border-primary/50" data-index={i}>
          {post.featuredImage && (
            <div className="relative h-48 w-full overflow-hidden">
              <img
                src={post.featuredImage}
                alt={post.title}
                className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </div>
          )}
          <CardHeader className="p-6 pb-2">
            <div className="flex items-center gap-2 text-xs font-mono text-primary uppercase tracking-widest mb-2">
              <Calendar className="h-3 w-3" />
              <span>{formatDate(post.date)}</span>
            </div>
            <CardTitle className="text-xl font-bold leading-tight group-hover:text-primary transition-colors line-clamp-2">
              <Link to={post.link || "#"}>
                <span dangerouslySetInnerHTML={{ __html: post.title }} />
              </Link>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 pt-2 flex-grow">
            <div 
              className="text-muted-foreground text-sm line-clamp-3 font-sans leading-relaxed"
              dangerouslySetInnerHTML={{ __html: post.excerpt || "" }} 
            />
          </CardContent>
          <CardFooter className="p-6 pt-0">
            <Link to={post.link || "#"} className="w-full">
              <Button variant="ghost" className="w-full justify-between group-hover:bg-primary group-hover:text-primary-foreground transition-all duration-300">
                Read Specification <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
          </CardFooter>
        </Card>
      ))}
    </div>
  );
}


function RelatedPostsSection({ currentPostId }: { currentPostId: string }) {
  const relatedQuery = useMemo(() => {
    return new WP_Query({
      post_type: "post",
      posts_per_page: 3,
      post__not_in: [parseInt(currentPostId) || 0],
      orderby: "date",
      order: "DESC",
      ignore_sticky_posts: true,
    });
  }, [currentPostId]);

  return (
    <div className="mt-24">
      <div className="flex items-center justify-between mb-10">
        <h2 className="text-3xl font-bold tracking-tighter text-foreground">Technical Insights & Innovation</h2>
        <Link to="#">
          <Button variant="outline" className="hidden sm:flex border-primary/20 hover:border-primary/50 hover:bg-primary/5">
            Browse All Insights
          </Button>
        </Link>
      </div>
      <PostsProvider wp_query={relatedQuery}>
        <RelatedPostsConsumer />
      </PostsProvider>
    </div>
  );
}


function SinglePostConsumer() {
  const { posts, loading, error, refetch } = usePosts();

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <div className="h-12 w-3/4 bg-muted/20 animate-pulse rounded-lg mb-6" />
        <div className="h-6 w-1/2 bg-muted/20 animate-pulse rounded-lg mb-12" />
        <div className="h-[500px] w-full bg-muted/20 animate-pulse rounded-xl mb-12" />
        <div className="space-y-4">
          <div className="h-4 w-full bg-muted/20 animate-pulse rounded" />
          <div className="h-4 w-full bg-muted/20 animate-pulse rounded" />
          <div className="h-4 w-5/6 bg-muted/20 animate-pulse rounded" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-20 text-center">
        <h2 className="text-2xl font-bold mb-4 text-foreground">Technical Error: Content Unavailable</h2>
        <p className="text-muted-foreground mb-6">The requested technical documentation could not be retrieved at this time.</p>
        <Button className="bg-primary text-primary-foreground hover:bg-primary/90" onClick={() => refetch()}>Try Again</Button>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="container mx-auto px-4 py-20 text-center">
        <h2 className="text-2xl font-bold mb-4 text-foreground">Document Not Found</h2>
        <p className="text-muted-foreground mb-6">The technical resource you are seeking is either moved or no longer available.</p>
        <Link to="#">
          <Button className="bg-primary text-primary-foreground hover:bg-primary/90">Return to Dashboard</Button>
        </Link>
      </div>
    );
  }

  const post = posts[0];

  return (
    <article className="container mx-auto px-4 py-12 md:py-20 max-w-5xl">
      
      <header className="mb-10 md:mb-14 text-center max-w-3xl mx-auto">
        <div className="flex items-center justify-center gap-2 text-sm font-bold text-primary mb-6">
          {post.categories && post.categories.length > 0 && (
            <span className="bg-primary/10 text-primary border border-primary/20 px-4 py-1 rounded-full uppercase tracking-widest text-xs">
              {post.categories[0].name}
            </span>
          )}
          <span className="text-muted-foreground flex items-center gap-1 font-mono">
            <Clock className="h-3.5 w-3.5" /> {formatDate(post.date)}
          </span>
        </div>
        
        <h1 
          className="text-4xl md:text-5xl lg:text-7xl font-extrabold tracking-tighter text-foreground mb-4 leading-[1.1]"
          dangerouslySetInnerHTML={{ __html: post.title }}
        />
        <h2 className="text-xl md:text-2xl font-bold text-muted-foreground mb-10 tracking-tight">
          Advanced Engineering Specifications & Infrastructure Analysis
        </h2>

        <div className="flex items-center justify-center gap-4">
          <div className="flex items-center gap-3">
            <Avatar className="h-12 w-12 border-2 border-primary/20 shadow-lg">
              <AvatarImage src={post.author?.avatar?.["96"] || ""} alt={post.author?.name} />
              <AvatarFallback className="bg-muted"><User className="h-6 w-6 text-primary" /></AvatarFallback>
            </Avatar>
            <div className="text-left">
              <p className="text-sm font-bold text-foreground tracking-tight">{post.author?.name || "Nexel Engineering"}</p>
              <p className="text-xs text-primary font-mono uppercase tracking-wider">Technical Lead</p>
            </div>
          </div>
        </div>
      </header>

      
      {post.featuredImage && (
        <div className="mb-12 md:mb-16 rounded-3xl overflow-hidden shadow-2xl border border-border/50 bg-muted/20 relative">
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-background/20" />
          <img 
            src={post.featuredImage} 
            alt={post.title} 
            className="w-full h-auto max-h-[750px] object-cover"
          />
        </div>
      )}

      
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        
        <div className="hidden lg:block lg:col-span-2">
          <div className="sticky top-32 space-y-6">
            <p className="text-xs font-black uppercase tracking-[0.2em] text-primary mb-4 opacity-70">Distribute</p>
            <Button variant="outline" size="icon" className="rounded-xl w-12 h-12 border-border/50 hover:border-primary hover:text-primary transition-colors" aria-label="Share on Twitter">
              <Share2 className="h-5 w-5" />
            </Button>
            
            <div className="h-12 w-12 rounded-xl border border-border/50 flex items-center justify-center text-muted-foreground text-xs font-bold cursor-pointer hover:border-primary hover:text-primary transition-all duration-300">
              FB
            </div>
            <div className="h-12 w-12 rounded-xl border border-border/50 flex items-center justify-center text-muted-foreground text-xs font-bold cursor-pointer hover:border-primary hover:text-primary transition-all duration-300">
              LI
            </div>
          </div>
        </div>

        
        <div className="lg:col-span-8">
          <div 
            className="prose prose-lg dark:prose-invert max-w-none 
              prose-headings:font-bold prose-headings:tracking-tighter prose-headings:text-foreground
              prose-p:text-muted-foreground prose-p:leading-relaxed prose-p:font-sans
              prose-a:text-primary prose-a:font-semibold prose-a:no-underline hover:prose-a:underline
              prose-img:rounded-2xl prose-img:shadow-xl prose-img:border prose-img:border-border/50
              prose-blockquote:border-l-primary prose-blockquote:bg-primary/5 prose-blockquote:py-4 prose-blockquote:px-8 prose-blockquote:rounded-r-2xl prose-blockquote:not-italic prose-blockquote:text-foreground/90"
            dangerouslySetInnerHTML={{ __html: post.content || "" }}
          />

          <div className="mt-12 pt-8 border-t border-border/50">
            <h3 className="text-xl font-bold text-foreground mb-4">External References</h3>
            <div className="flex flex-col gap-2">
              <Link 
                to="https://www.itu.int/rec/T-REC-G.657/en" 
                newTab 
                className="text-primary font-semibold no-underline hover:underline w-fit"
              >
                ITU‑T G.657 Recommendation
              </Link>
              <Link 
                to="https://www.thefoa.org/" 
                newTab 
                className="text-primary font-semibold no-underline hover:underline w-fit"
              >
                The Fiber Optic Association
              </Link>
            </div>
          </div>

          
          {post.tags && post.tags.length > 0 && (
            <div className="mt-12 pt-8 border-t border-border/50">
              <div className="flex flex-wrap gap-2 items-center">
                <Tag className="h-4 w-4 text-primary mr-2" />
                {post.tags.map((tag) => (
                  <Link key={tag.id} to={tag.link || "#"}>
                    <span className="inline-flex items-center px-4 py-1.5 rounded-lg text-xs font-mono bg-primary/5 text-primary border border-primary/20 hover:bg-primary hover:text-primary-foreground transition-all duration-300">
                      #{tag.name.toUpperCase()}
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          )}

          
          <div className="mt-12 p-8 bg-card/40 rounded-3xl border border-border/50 backdrop-blur-sm shadow-xl">
            <div className="flex flex-col sm:flex-row gap-6 items-center sm:items-start text-center sm:text-left">
              <Avatar className="h-24 w-24 border-4 border-primary/10 shadow-lg">
                <AvatarImage src={post.author?.avatar?.["96"] || ""} alt={post.author?.name} />
                <AvatarFallback className="bg-muted"><User className="h-10 w-10 text-primary" /></AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-foreground mb-2">Lead Engineer: {post.author?.name}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed mb-4 font-sans">
                  
                  Senior Technical Consultant at Nexel Technologies. Expert in AI data center infrastructure, high-precision FPV drone systems, and next-generation optical fiber solutions including G652D and G657A2 standards.
                </p>
                <Link to={post.author?.link || "#"}>
                  <Button variant="link" className="p-0 h-auto text-primary font-bold hover:no-underline group">
                    View technical portfolio <ChevronRight className="h-4 w-4 ml-1 transition-transform group-hover:translate-x-1" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      
      <Separator className="my-16 opacity-30" />
      <RelatedPostsSection currentPostId={post.id} />
    </article>
  );
}


export default function SinglePostTemplateSection() {
  
  
  const currentParams = wvcClient.getCurrentWPQueryParams();
  const wpQuery = new WP_Query(currentParams);

  return (
    <section id="single-post-template-section" className="dark bg-background text-foreground min-h-screen selection:bg-primary/30 selection:text-primary">
      <PostsProvider wp_query={wpQuery}>
        <SinglePostConsumer />
      </PostsProvider>
    </section>
  );
}